import { ALLOWED_DOCUMENT_EXTENSIONS } from '../constants/fileTypes'

// Shared helpers for Content Library items: deterministic Storage paths and
// download filename construction. Reused by uploads, downloads and the
// backoffice so a single change in policy applies everywhere.
//
// The sanitization rule MUST stay in sync with the backend mirror at
// functions/src/contentLibrary/originalsPath.js — uploads (frontend) and the
// download stream (backend) both derive the Storage path from
// `documentFileName` alone, so any divergence would silently break downloads.

const ORIGINALS_ROOT = 'content-library'
const TEMPLATES_PREFIX = `${ORIGINALS_ROOT}/templates`
const FILENAME_SLUG_PATTERN = /[^a-zA-Z0-9]/g
// Filesystem-illegal characters that must be stripped before handing a name
// to the browser's `download` attribute or to Content-Disposition.
const ILLEGAL_FILENAME_CHARS_PATTERN = /[\x00-\x1F\x7F\\/"<>|:*?]/g

// Trusted trailing suffixes: same catalogue as uploads (ALLOWED_DOCUMENT_EXTENSIONS).
// Tokens that equal `targetExtension` are trusted even when not in that set so
// legacy names like "*.csv" or "*.txt" still normalise cleanly.

const isTrustedTrailingExtension = (trailingToken, targetExtensionLower) => {
  const tokenLower = trailingToken.toLowerCase()
  if (targetExtensionLower && tokenLower === targetExtensionLower) {
    return true
  }
  return ALLOWED_DOCUMENT_EXTENSIONS.has(tokenLower)
}

// Mirrors functions/scripts/migrateContentLibraryItems.js: collapse runs of
// `_` and trim leading/trailing `_`. The CSV bootstrap that seeded most of
// production already used this rule, so any divergence here would point the
// downloader at non-existent Storage paths for those items.
export const sanitizeContentLibraryFileName = (documentFileName) => {
  const fileName = String(documentFileName || '')
  const dotIndex = fileName.lastIndexOf('.')
  const baseName = dotIndex > 0 ? fileName.slice(0, dotIndex) : fileName
  const extension = dotIndex > 0 ? fileName.slice(dotIndex + 1) : ''
  const cleanName = baseName
    .replace(FILENAME_SLUG_PATTERN, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_|_$/g, '')
  return { cleanName, extension }
}

export const getContentLibraryOriginalPath = (documentFileName) => {
  return getContentLibraryStoragePath(documentFileName, { isTemplate: false })
}

export const getContentLibraryTemplatePath = (documentFileName) => {
  return getContentLibraryStoragePath(documentFileName, { isTemplate: true })
}

export const getContentLibraryStoragePath = (documentFileName, { isTemplate = false } = {}) => {
  const { cleanName, extension } = sanitizeContentLibraryFileName(documentFileName)
  if (!cleanName) {
    return ''
  }
  const root = isTemplate ? TEMPLATES_PREFIX : ORIGINALS_ROOT
  return extension
    ? `${root}/${cleanName}.${extension}`
    : `${root}/${cleanName}`
}

export const CONTENT_LIBRARY_ORIGINALS_ROOT = ORIGINALS_ROOT
export const CONTENT_LIBRARY_TEMPLATES_ROOT = TEMPLATES_PREFIX

// Display-name priority for anything the end user sees (download filename,
// list titles, etc.). `documentDisplayName` is the curated label set by
// admins; `libraryFileName` is the legacy field; `documentFileName` is the
// raw upload name and the last-resort fallback.
export const pickContentLibraryRawName = (item) => (
  String(item?.documentDisplayName || item?.libraryFileName || item?.documentFileName || 'document')
)

// Builds the filename used by the browser when saving a Content Library
// document. When `format` is provided (branding engine flow: PDF/DOCX rendered
// on demand) the file is named with that extension; otherwise the extension
// is taken from `documentFileName`.
//
// Only tokens after the final dot that match the requested extension or appear
// in ALLOWED_DOCUMENT_EXTENSIONS are stripped/replaced — so dots in titles
// (dates, versions, "Handbook.2024") are preserved unless they are recognised
// upload types.
export const buildContentLibraryDownloadFileName = (item, { format } = {}) => {
  const rawName = pickContentLibraryRawName(item)
  const sanitized = rawName.replace(ILLEGAL_FILENAME_CHARS_PATTERN, '').trim() || 'document'

  const targetExtension = (() => {
    if (format) return String(format).toLowerCase()
    const documentFileName = String(item?.documentFileName || '')
    const dotIndex = documentFileName.lastIndexOf('.')
    return dotIndex > 0 ? documentFileName.slice(dotIndex + 1).toLowerCase() : ''
  })()

  const trailingDot = sanitized.lastIndexOf('.')
  const trailingToken = trailingDot >= 0 ? sanitized.slice(trailingDot + 1) : ''
  const hasTrustedExtension =
    trailingToken.length > 0 &&
    isTrustedTrailingExtension(trailingToken, targetExtension)

  if (hasTrustedExtension) {
    if (!targetExtension || trailingToken.toLowerCase() === targetExtension) {
      return sanitized
    }
    const baseName = sanitized.slice(0, trailingDot)
    return `${baseName}.${targetExtension}`
  }

  if (!targetExtension) {
    return sanitized
  }
  if (sanitized.toLowerCase().endsWith(`.${targetExtension}`)) {
    return sanitized
  }
  return `${sanitized}.${targetExtension}`
}

export const normalizeContentLibraryEmbedUrl = (embedUrl) => {
  const trimmedEmbedUrl = String(embedUrl || '').trim()
  if (!trimmedEmbedUrl) {
    return ''
  }

  try {
    const parsedUrl = new URL(trimmedEmbedUrl)
    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      return ''
    }
    return parsedUrl.toString()
  } catch {
    return ''
  }
}

export const isEmbeddableContentLibraryUrl = (embedUrl) => {
  return Boolean(normalizeContentLibraryEmbedUrl(embedUrl))
}

// Normalizes any of the date shapes a Content Library record can carry into a
// plain Date, or null when the value is absent or unparseable.
//
// Two sources feed the same readers: the per-user Firestore subcollections
// (contentLibraryFavorites / contentLibraryDownloads) store Firestore
// Timestamps, while the Realtime Database items keep ISO strings — and the
// subcollections themselves hold both types while the migration to Timestamps
// rolls out. `new Date(timestamp)` yields an Invalid Date, so every reader has
// to go through here instead.
export const toContentLibraryDate = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  // Firestore Timestamp.
  if (typeof value.toDate === 'function') {
    const converted = value.toDate()
    return isNaN(converted.getTime()) ? null : converted
  }

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const parsed = new Date(value)
    return isNaN(parsed.getTime()) ? null : parsed
  }

  return null
}
